import { useEffect, useState } from "react";
import styled from "@emotion/styled";
import { GrButton, GrDivider, GrIconButton, GrMenu, GrMenuItem, GrStack, palette, typography } from "@jnpmedi/grebob";
import { DocsAuthTokenManager } from "@newLayouts/auth/DocsAuthTokenManager";
import { langHelper } from "@newLibs/utils";
import { useSignOut } from "@newHooks/auth/useSignOut";
import { UseBoolean } from "@newHooks/common/useBoolean";
import { useLayoutContext } from "@newHooks/context/layout/layout.context";
import { useMyInfoContext } from "@newHooks/context/myInfo/myInfo.context";
import { useDeleteTokenMutation } from "@newHooks/mutation/auth/deleteToken.mutation";
import { useMyInfoQuery } from "@newHooks/query/myInfo/myInfo.query";
import baseMedia from "@assets/theme/baseDefine/baseMedia";
import { baseZIndex } from "@assets/theme/baseDefine/baseZIndex";
import { baseHeight } from "@assets/theme/baseSize/baseHeight";
import { baseWidth } from "@assets/theme/baseSize/baseWidth";
import { MvnLanguageText, useHeader } from "./hooks/useHeader";
import { LoLang, MavenService, readableLoLang } from "@maven-rest/common/common.types";
import { ApiErrorResponse } from "@maven-rest/common/flax.types";
import { RestError } from "@maven-rest/common/HttpRequest";

export function Header() {
  const language = langHelper.getLang();
  const signOut = useSignOut();

  const { sidebarCollapsed, isHiddenSideBar } = useLayoutContext();
  const { myInfo: currentUser } = useMyInfoContext();

  const { handleChangeLocaleLanguage } = useHeader();

  const [languageEl, setLanguageEl] = useState<HTMLElement | null>(null);
  const [profileEl, setProfileEl] = useState<HTMLElement | null>(null);

  const { data: userData, error: userError } = useMyInfoQuery();
  const { mutate: logout, isLoading } = useDeleteTokenMutation();

  const handleLogout = () => {
    if (isLoading) return;

    logout({ service: MavenService.MavenDocs }, { onSuccess: () => signOut() });
  };

  const dropdownItems: GrMenuItem[] = [
    {
      type: "item",
      text: "Profile",
      icon: { type: "user" },
      onClick: () => window.open(`${process.env.REACT_APP_AUTH_FE_URL}/profile`),
    },
    {
      type: "item",
      text: "Account History",
      icon: { type: "history" },
      onClick: () => window.open(`${process.env.REACT_APP_AUTH_FE_URL}/user-auth-logs`),
    },
    { type: "divider" },
    {
      type: "item",
      text: "Organizations",
      icon: { type: "users-stroke" },
      onClick: () => window.open(`${process.env.REACT_APP_AUTH_FE_URL}/organizations`),
    },
    {
      type: "item",
      text: "Subscription",
      icon: { type: "credit-card" },
      onClick: () => window.open(`${process.env.REACT_APP_AUTH_FE_URL}/subscription/dashboard`),
    },
    {
      type: "item",
      text: "Invoice",
      icon: { type: "file-text" },
      onClick: () => window.open(`${process.env.REACT_APP_AUTH_FE_URL}/invoices`),
    },
  ];

  dropdownItems.push({
    type: "item",
    text: "Logout",
    icon: { type: "logout" },
    onClick: handleLogout,
  });

  useEffect(() => {
    if (!userError || !(userError instanceof RestError)) return;

    const error = userError.response as ApiErrorResponse;

    if (error.code === "CREDENTIAL-INVALID" || error.code === "CREDENTIAL-INVALID-BY-NEW-TOKEN") {
      signOut();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userError]);

  return (
    <HeaderWrapper sidebarCollapsed={sidebarCollapsed} isHiddenSidebar={isHiddenSideBar}>
      <HeaderItemsWrapper>
        <HeaderItemsNavigator flexDirection="row">
          <TextButton
            variant="text"
            startIcon="world"
            endIcon={languageEl ? "chevron-up" : "chevron-down"}
            color="white"
            onClick={(e) => setLanguageEl(e.currentTarget)}
          >
            {readableLoLang(language)}
          </TextButton>
          <Divider />
          <QuestionMarkIconButton
            iconType="question-mark"
            color="white"
            size={16}
            onClick={() => window.open("https://mvn.do/guides/docs")}
          />

          {!!currentUser && (
            <>
              <Divider />
              <DocsAuthTokenManager />
              <TextButton
                variant="text"
                endIcon={profileEl ? "chevron-up" : "chevron-down"}
                color="white"
                onClick={(e) => setProfileEl(e.currentTarget)}
              >
                {userData?.name || userData?.email}
              </TextButton>
            </>
          )}
        </HeaderItemsNavigator>
        <GrMenu
          anchorElement={languageEl}
          position={{ vertical: "bottom", horizontal: "right" }}
          items={[
            {
              onClick: () => handleChangeLocaleLanguage(LoLang.EN),
              text: MvnLanguageText.EN,
              type: "item",
            },
            {
              onClick: () => handleChangeLocaleLanguage(LoLang.KO),
              text: MvnLanguageText.KO,
              type: "item",
            },
          ]}
          onClose={() => setLanguageEl(null)}
        />
        <GrMenu
          anchorElement={profileEl}
          position={{ vertical: "bottom", horizontal: "right" }}
          size="small"
          items={dropdownItems}
          onClose={() => setProfileEl(null)}
        />
      </HeaderItemsWrapper>
    </HeaderWrapper>
  );
}

const HeaderWrapper = styled("header")<{ sidebarCollapsed: UseBoolean; isHiddenSidebar: UseBoolean }>(
  ({ sidebarCollapsed, isHiddenSidebar }) => ({
    position: "fixed",
    top: 0,
    right: 0,
    left: 0,
    height: baseHeight.appHeaderHeight,
    zIndex: baseZIndex.layout.header,
    minWidth: baseWidth.appHeaderMinWidth,
    background: palette["maven-blue-bright"],
    color: palette.white,
    transition: "left ease-in-out 0.23s",
    ...Object.assign({}, typography["Content.Body04"] || {}),

    "@media (max-width: 1024px)": {
      left: !isHiddenSidebar ? baseWidth.appSidebarWidthCollapsed : 0,
    },

    [baseMedia.mobile]: {
      minWidth: baseWidth.appHeaderMinWidthMobile,
    },

    ...(sidebarCollapsed.value && {}),
  })
);

const HeaderItemsWrapper = styled(GrStack)`
  align-items: center;
  justify-content: flex-end;
  width: 100%;
  height: 100%;
`;

const HeaderItemsNavigator = styled(GrStack)`
  float: right;
  align-items: center;
  padding-right: 16px;

  & > div {
    display: flex;
    align-items: center;
  }
`;

const Divider = styled(GrDivider)`
  width: 1px;
  height: 20px;
  margin: 0px 8px !important;
  border-color: transparent !important;
  background-color: ${palette["blue040"]};
`;

const TextButton = styled(GrButton)`
  &:hover {
    background-color: transparent;
  }

  &:active {
    background-color: transparent;
  }
`;

const QuestionMarkIconButton = styled(GrIconButton)`
  &:hover {
    background-color: ${palette["maven-blue-bright"]};
  }

  &:active {
    background-color: transparent;
  }
`;
